import { Observable } from 'rxjs';

/**
 * The site's content API.
 *
 * Every read the site performs against the CMS is one of these requests. They
 * are deliberately narrow: the browser names an operation, never an upstream
 * address, a collection or a filter. The upstream URL is built on the server
 * (see `src/strapi-content.server.ts`), which is also the only place that holds
 * the CMS credential.
 */

export const LEGAL_DOCUMENTS = ['privacy-policy', 'terms-of-service'] as const;

export type LegalDocument = (typeof LEGAL_DOCUMENTS)[number];

export type ContentRequest =
  | { kind: 'posts'; category?: string }
  | { kind: 'post'; slug: string }
  | { kind: 'categories' }
  | { kind: 'legal'; document: LegalDocument };

export const CONTENT_API_PREFIX = '/api/content';

export function isLegalDocument(value: string): value is LegalDocument {
  return (LEGAL_DOCUMENTS as readonly string[]).includes(value);
}

/** Same-origin address the browser uses for a content request. */
export function contentRequestPath(request: ContentRequest): string {
  switch (request.kind) {
    case 'posts':
      return request.category
        ? `${CONTENT_API_PREFIX}/posts?category=${encodeURIComponent(request.category)}`
        : `${CONTENT_API_PREFIX}/posts`;
    case 'post':
      return `${CONTENT_API_PREFIX}/posts/${encodeURIComponent(request.slug)}`;
    case 'categories':
      return `${CONTENT_API_PREFIX}/categories`;
    case 'legal':
      return `${CONTENT_API_PREFIX}/legal/${request.document}`;
  }
}

/**
 * Transport for content requests. The browser goes through this server over
 * HTTP; during server-side rendering the same request is answered in process,
 * without a round trip.
 */
export abstract class ContentGateway {
  abstract fetch<T>(request: ContentRequest): Observable<T>;
}
